import React, { useState, useEffect } from 'react';
import adminAPI from '../../lib/api';
import { Users, Activity, MessageSquare, Target, TrendingUp, TrendingDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

function KpiCard({ icon: Icon, label, value, sub, trend, color = '#CCFF00' }) {
  return (
    <div data-testid="admin-kpi-card" className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5 hover:bg-white/[0.04] hover:border-white/10 transition-[background-color,border-color] duration-180">
      <div className="flex items-start justify-between mb-4">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs ${trend >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
            {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-1">{label}</p>
      <p className="font-['Barlow_Condensed'] font-bold text-4xl text-white">{value}</p>
      {sub && <p className="text-xs text-white/30 mt-1">{sub}</p>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-[12px] bg-[#121212] border border-white/10 p-3 shadow-lg">
      <p className="text-xs text-white/50 uppercase tracking-wider mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-medium" style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [daily, setDaily] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, dl] = await Promise.all([
        adminAPI.getAnalyticsOverview(),
        adminAPI.getAnalyticsDaily(14)
      ]);
      setOverview(ov);
      setDaily(Array.isArray(dl) ? dl : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="text-center py-20">
      <p className="text-[#FF3B30] mb-4">{error}</p>
      <button onClick={loadData} className="text-sm text-[#CCFF00] hover:underline">Prøv igjen</button>
    </div>
  );

  const u = overview?.users || {};
  const e = overview?.engagement || {};
  const c = overview?.completion || {};

  return (
    <div data-testid="admin-dashboard-kpi-grid">
      <div className="mb-6 sm:mb-8">
        <h1 className="font-['Barlow_Condensed'] font-bold text-2xl sm:text-3xl md:text-4xl text-white">Dashboard</h1>
        <p className="text-xs sm:text-sm text-white/40 mt-1">Oversikt over Tempo-plattformen</p>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard icon={Users} label="Totalt brukere" value={u.total || 0} sub={`${u.new_this_week || 0} nye denne uken`} color="#CCFF00" />
        <KpiCard icon={Activity} label="DAU" value={e.dau || 0} sub={`${e.dau_yesterday || 0} i går`} color="#5AC8FA" />
        <KpiCard icon={MessageSquare} label="Chat i dag" value={e.chat_messages_today || 0} color="#34C759" />
        <KpiCard icon={Target} label="Fullføringsrate" value={`${Math.round((c.rate || 0) * 100)}%`} sub={`${c.completed_today || 0}/${c.total_plans_today || 0} i dag`} color="#FF9F0A" />
      </div>

      {/* Distribution */}
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5">
          <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-4">MÅLFORDELING</p>
          <div className="space-y-3">
            {(overview?.distribution?.goals || []).map((g, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-white/70 capitalize">{g.goal?.replace(/_/g, ' ') || 'Ukjent'}</span>
                <span className="font-['Barlow_Condensed'] font-bold text-lg text-white">{g.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5">
          <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-4">ERFARINGSNIVÅ</p>
          <div className="space-y-3">
            {(overview?.distribution?.experience || []).map((e, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-white/70 capitalize">{e.level || 'Ukjent'}</span>
                <span className="font-['Barlow_Condensed'] font-bold text-lg text-white">{e.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts */}
      {daily.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          <div data-testid="admin-dashboard-daily-active-chart" className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5">
            <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-4">AKTIVE BRUKERE</p>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} stroke="rgba(255,255,255,0.18)" />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} stroke="rgba(255,255,255,0.18)" />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="active_users" stroke="#CCFF00" strokeWidth={2} dot={false} name="Aktive" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5">
            <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-4">ØKTER FULLFØRT</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} stroke="rgba(255,255,255,0.18)" />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} stroke="rgba(255,255,255,0.18)" />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="workouts_completed" fill="#5AC8FA" radius={[4, 4, 0, 0]} name="Fullført" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Quick stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-4 text-center">
          <p className="uppercase tracking-[0.18em] text-[10px] text-white/30 mb-1">ONBOARDET</p>
          <p className="font-['Barlow_Condensed'] font-bold text-2xl text-[#34C759]">{u.onboarded || 0}</p>
        </div>
        <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-4 text-center">
          <p className="uppercase tracking-[0.18em] text-[10px] text-white/30 mb-1">DEAKTIVERT</p>
          <p className="font-['Barlow_Condensed'] font-bold text-2xl text-[#FF3B30]">{u.disabled || 0}</p>
        </div>
        <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-4 text-center">
          <p className="uppercase tracking-[0.18em] text-[10px] text-white/30 mb-1">WAU</p>
          <p className="font-['Barlow_Condensed'] font-bold text-2xl text-[#5AC8FA]">{e.wau || 0}</p>
        </div>
        <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-4 text-center">
          <p className="uppercase tracking-[0.18em] text-[10px] text-white/30 mb-1">NYE DENNE UKA</p>
          <p className="font-['Barlow_Condensed'] font-bold text-2xl text-[#CCFF00]">{u.new_this_week || 0}</p>
        </div>
      </div>
    </div>
  );
}
